import { compact } from 'lodash';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import type { StudioAnalysisRequest, StudioRequestSubject, Subject } from 'venn-state';
import { fundSubject, portfolioSubject, subjectsDateRanges } from 'venn-state';
import type { ComputedDateRange } from 'venn-utils';
import { assertExhaustive, getSubjectFromRequestSubject } from 'venn-utils';

const getUniqueKeyFromSubject = (subject: Subject): string => {
  switch (subject.type) {
    case 'fund':
      return subject.fundId;
    case 'private-fund':
      return subject.privateFundId;
    case 'private-portfolio':
      return subject.privatePortfolioId;
    case 'portfolio':
      return `${subject.portfolioId}${subject.portfolioVersion ? `-${subject.portfolioVersion}` : ''}`;
    default:
      return assertExhaustive(subject);
  }
};

export const getUniqueKeyFromStudioRequestSubject = (subject: StudioRequestSubject): string =>
  getUniqueKeyFromSubject(getSubjectFromRequestSubject(subject));

export const getRangeMapKeyFromRequest = (request: StudioAnalysisRequest): string =>
  getUniqueKeyFromStudioRequestSubject(request.subject);

export const useRequestsRangeMap = (requests: StudioAnalysisRequest[]): Record<string | number, ComputedDateRange> => {
  const allSubjects = useMemo(() => {
    const subjects = requests.map((request) => getSubjectFromRequestSubject(request.subject));
    // TODO: VENN-21001: fix case where we have an instrument-only portfolio (usually as individual benchmark)
    const benchmarks = compact(
      requests.map((request): Subject | undefined => {
        if (request.benchmark?.fund) {
          return fundSubject(request.benchmark.fund.id);
        }
        if (request.benchmark?.portfolio) {
          return portfolioSubject(request.benchmark.portfolio.id);
        }
        return undefined;
      }),
    );
    return [...subjects, ...benchmarks];
  }, [requests]);

  const ranges = useRecoilValue(subjectsDateRanges(allSubjects));

  const map = useMemo(() => {
    const allEntries = ranges.map((range, i) => [getUniqueKeyFromSubject(allSubjects[i]!), range]);
    return Object.fromEntries(allEntries.filter(([key]) => key));
  }, [allSubjects, ranges]);

  return map;
};
